import { useMemo, useState } from "react";
import { CollectionIcon } from "@heroicons/react/outline";

import { LanguageType } from "../CodeEditor/types";
import { snippetLanguages } from "../CodeEditor/constants";
import CodeEditor from "../CodeEditor";
import { Button } from "../ui/button";

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";

interface CodeHelpModalProps {
  code: Partial<Record<LanguageType, string>>;
  title: string;
  description: string;
  docsLink: string;
  buttonText: string;
}

export const CodeHelpModal = ({
  code,
  title,
  description,
  docsLink,
  buttonText,
}: CodeHelpModalProps) => {
  const [open, setOpen] = useState(false);
  const availableLanguages = useMemo(
    () => snippetLanguages.filter(language => Boolean(code[language]?.trim())),
    [code],
  );
  const [selectedLanguage, setSelectedLanguage] = useState<LanguageType>(
    availableLanguages[0] ?? snippetLanguages[0],
  );
  const displayedLanguage =
    availableLanguages.find(language => language === selectedLanguage) ?? availableLanguages[0];

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <button
          type="button"
          className="inline-flex items-center rounded-full border border-gray-200 bg-white px-2.5 py-1 text-xs text-gray-500 shadow-sm transition-colors hover:border-gray-300 hover:bg-gray-50 hover:text-gray-700"
          aria-label={buttonText}
        >
          <CollectionIcon className="mr-1 h-4 w-4" />
          <span>{buttonText}</span>
        </button>
      </DialogTrigger>
      <DialogContent className="max-w-4xl overflow-hidden p-0">
        <DialogHeader className="space-y-3 border-b border-gray-200 px-6 py-5 text-left">
          <div className="space-y-1">
            <DialogTitle className="flex items-end gap-2">
              {title}
              <a
                href={docsLink}
                target="_blank"
                rel="noreferrer"
                className="w-fit text-xs text-blue-600 hover:text-blue-800"
              >
                Read docs
              </a>
            </DialogTitle>
            <DialogDescription className="text-sm text-gray-600">{description}</DialogDescription>
          </div>
        </DialogHeader>
        <div className="px-6 py-5">
          {displayedLanguage ? (
            <div className="overflow-hidden rounded-lg border border-gray-200">
              <CodeEditor
                showRuntimeDetails={false}
                languages={availableLanguages}
                editorOptions={{
                  readOnly: true,
                  fontSize: 12,
                  lineHeight: 18,
                  padding: { top: 12, bottom: 12 },
                  renderLineHighlight: "none",
                }}
                selectedLanguage={displayedLanguage}
                handleLanguageChange={setSelectedLanguage}
                code={code[displayedLanguage] ?? ""}
                height="320px"
              />
            </div>
          ) : (
            <div className="rounded-lg border border-dashed border-gray-200 px-4 py-6 text-sm text-gray-500">
              No code example available.
            </div>
          )}
        </div>
        <DialogFooter className="border-t border-gray-200 px-6 py-4 sm:justify-end">
          <Button onClick={() => setOpen(false)}>Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
